import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Sentry } from '../lib/sentry';
import { analytics } from '../lib/analytics';
import { mcpService } from '../services/mcpService';
import { cache } from '../utils/cache';
import { useSubscriptionStore } from './subscriptionStore';
import { useNotificationStore } from './notificationStore';

// Wipe every per-user cache so data from one account cannot leak into the next
// session when another user logs in on the same browser tab. The React Query
// client is reached via the `window.__qc` handle published in App.tsx, the
// same pattern the axios interceptor uses to invalidate on 402.
function clearPerUserCaches(): void {
  try {
    const qc = (window as unknown as { __qc?: { clear: () => void } }).__qc;
    qc?.clear();
  } catch {
    // Non-fatal: QueryClient may not be initialized yet (e.g., very early in boot).
  }
  try {
    useNotificationStore.getState().reset();
  } catch {
    // Silently ignore if store not yet initialized.
  }
  try {
    useSubscriptionStore.getState().reset();
  } catch {
    // Silently ignore if store not yet initialized.
  }
  try {
    cache.clear();
  } catch {
    // Non-fatal.
  }
  try {
    mcpService.clearActiveKey();
  } catch {
    // Non-fatal.
  }
}

export interface User {
  id: string;
  email: string;
  name: string; // backend returns full_name, need to map or rename
  role: string;
  companyId: string; // backend returns company_id
  emailNotificationsEnabled: boolean; // backend returns email_notifications_enabled
}

export interface Permission {
  obj: string;
  act: string;
}

export interface Company {
  id: string;
  name: string;
  code: string;
  address: string;
  email?: string;
  phone?: string;
  website?: string;
  currency_symbol?: string;
}

interface AuthState {
  user: User | null;
  company: Company | null;
  token: string | null;
  permissions: Permission[];
  companyCanEditRoles: boolean;
  isAuthenticated: boolean;
  login: (user: User, company: Company, token: string, permissions?: Permission[], canEditRoles?: boolean) => void;
  syncProfile: (user: User, company: Company, permissions?: Permission[], canEditRoles?: boolean) => void;
  setCompany: (company: Company) => void;
  setPermissions: (permissions: Permission[]) => void;
  setCompanyCanEditRoles: (value: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      company: null,
      token: null,
      permissions: [],
      companyCanEditRoles: false,
      isAuthenticated: false,
      login: (user, company, token, permissions, canEditRoles) => {
        // Defensive: clear any caches left from a prior session before the
        // new user's state lands, in case logout was bypassed (session
        // expiry on another tab, hard reload mid-flow, etc.).
        clearPerUserCaches();
        localStorage.setItem('token', token);
        set({ user, company, token, permissions: permissions ?? [], companyCanEditRoles: canEditRoles ?? false, isAuthenticated: true });
        // Observability calls are best-effort — failures must not block the login flow.
        try {
          Sentry.setUser({ id: user.id, email: user.email, username: user.name });
          analytics.identify(user.id, {
            $email: user.email,
            $name: user.name,
            role: user.role,
            company: company.name,
            companyId: company.id,
          });
          analytics.track('Login');
        } catch {
          // Silently ignore; analytics/Sentry are non-critical.
        }
      },
      syncProfile: (user, company, permissions, canEditRoles) => {
        set((state) => ({
          ...state,
          user,
          company,
          permissions: permissions ?? state.permissions,
          companyCanEditRoles: canEditRoles ?? state.companyCanEditRoles,
          isAuthenticated: Boolean(state.token),
        }));
      },
      setPermissions: (permissions) => set({ permissions }),
      setCompany: (company) => set({ company }),
      setCompanyCanEditRoles: (value) => set({ companyCanEditRoles: value }),
      logout: () => {
        localStorage.removeItem('token');
        // Drop React Query cache and all per-user stores so a subsequent
        // login on the same tab cannot read or edit the previous user's data.
        clearPerUserCaches();
        set({ user: null, company: null, token: null, permissions: [], companyCanEditRoles: false, isAuthenticated: false });
        // Best-effort cleanup of observability sessions.
        try {
          Sentry.setUser(null);
          analytics.track('Logout');
          analytics.reset();
        } catch {
          // Silently ignore.
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        company: state.company,
        token: state.token,
        permissions: state.permissions,
        companyCanEditRoles: state.companyCanEditRoles,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
);
